"use client";

import useInteractionTracker from '@blueshift-ui/telemetry/dist/hooks/use-interaction-tracker';
import { BookOpen, Rocket, Sprout, Zap } from 'lucide-react';
import { memo, useMemo, useState } from 'react';

import type { VideoData } from '../types/video';

import { renderTextWithMathAndHTML } from '../utils/math-text-renderer';
import IndividualVideoPlayer from './individual-video-player';

interface ExplanationLevels {
  easy: string;
  hard: string;
  medium: string;
}

interface DificultyExplanationSliderProps {
  className?: string;
  conceptVideo?: VideoData;
  conceptVideoId?: string;
  explanations: ExplanationLevels;
  title?: string;
}

const DificultyExplanationSlider = memo(
  ({
    className = '',
    conceptVideo,
    conceptVideoId,
    explanations,
    title = 'Concept Explanation',
  }: DificultyExplanationSliderProps) => {
    const [level, setLevel] = useState<number>(0); // Default to easy (0)
    const { trackEvent } = useInteractionTracker();

    const levelData = [
      {
        bgColor:
          'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30',
        borderColor: 'border-green-200 dark:border-green-700',
        color: 'text-green-600 dark:text-green-400',
        description: 'Start here! Easy to understand',
        icon: Sprout,
        label: 'Beginner',
      },
      {
        bgColor:
          'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30',
        borderColor: 'border-amber-200 dark:border-amber-700',
        color: 'text-amber-600 dark:text-amber-400',
        description: 'Level up your skills',
        icon: Zap,
        label: 'Intermediate',
      },
      {
        bgColor:
          'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30',
        borderColor: 'border-red-200 dark:border-red-700',
        color: 'text-red-600 dark:text-red-400',
        description: 'Master level challenge',
        icon: Rocket,
        label: 'Advanced',
      },
    ];

    // Memoize explanation content to prevent unnecessary recalculations
    const currentExplanation = useMemo(() => {
      switch (level) {
        case 0:
          return explanations.easy;
        case 1:
          return explanations.medium;
        case 2:
          return explanations.hard;
        default:
          return explanations.medium;
      }
    }, [level, explanations]);

    const currentLevelData = levelData[level];
    const CurrentIcon = currentLevelData.icon;

    const handleSetLevel = (newLevel: number) => {
      const fromLabel = levelData[level].label.toLowerCase();
      const toLabel = levelData[newLevel].label.toLowerCase();
      setLevel(newLevel);
      void trackEvent({
        action: 'set-difficulty',
        category: 'page',
        label: `difficulty-switch-${fromLabel}-to-${toLabel}`,
      });
    };

    return (
      <section className={`mb-10 ${className} text-left`}>
        <div className="surface-card rounded-xl shadow-lg border border-subtle overflow-hidden">
          <div className="h-2 gradient-primary" />
          <div className="p-6 text-left">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <BookOpen aria-hidden="true" className="w-6 h-6 text-brand-primary" />
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-bold text-primary text-left">
                    {title}
                  </h2>
                  <p className="text-sm text-secondary text-left">
                    Choose your learning level
                  </p>
                </div>
              </div>
            </div>

            {conceptVideo && conceptVideoId && conceptVideo.src && (
              <div className="mb-6 explanation-box no-math">
                <div className="text-center mb-4">
                  <h4 className="font-semibold text-primary text-lg">
                    Watch & Learn
                  </h4>
                  <p className="text-base text-secondary">
                    Video explanation of this concept
                  </p>
                </div>

                <IndividualVideoPlayer
                  description={conceptVideo.description}
                  lazy={false}
                  src={conceptVideo.src}
                  thumbnail={conceptVideo.thumbnail}
                  title={conceptVideo.title}
                  videoId={conceptVideoId}
                />
              </div>
            )}

            <div
              className={`bg-gradient-to-r ${currentLevelData.bgColor} border ${currentLevelData.borderColor} rounded-xl p-6 mb-6 text-left`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <CurrentIcon
                    className={`w-6 h-6 sm:w-8 sm:h-8 ${currentLevelData.color}`}
                  />
                  <div className="min-w-0 flex-1">
                    <span
                      className={`font-bold text-lg sm:text-xl ${currentLevelData.color} text-left block`}
                    >
                      {currentLevelData.label}
                    </span>
                    <p className="text-xs sm:text-base text-secondary text-left">
                      {currentLevelData.description}
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative mb-7">
                <input
                  aria-label="Choose difficulty level"
                  aria-valuemax={2}
                  aria-valuemin={0}
                  aria-valuenow={level}
                  aria-valuetext={levelData[level].label}
                  className="w-full h-2 sm:h-3 surface-interactive rounded-full appearance-none cursor-pointer slider focus-ring-info"
                  max="2"
                  min="0"
                  onChange={e => handleSetLevel(Number(e.target.value))}
                  role="slider"
                  style={{
                    background: `linear-gradient(to right, ${
                      level === 0
                        ? '#22c55e'
                        : level === 1
                          ? '#eab308'
                          : '#ef4444'
                    } 0%, ${
                      level === 0
                        ? '#22c55e'
                        : level === 1
                          ? '#eab308'
                          : '#ef4444'
                    } ${(level / 2) * 100}%, #475569 ${(level / 2) * 100}%, #475569 100%)`,
                  }}
                  type="range"
                  value={level}
                />
                <div className="flex justify-between text-xs text-body mt-2 sm:mt-3">
                  {levelData.map((data, index) => {
                    const IconComponent = data.icon;
                    return (
                      <button
                        aria-pressed={level === index}
                        className={`flex flex-col items-center gap-1 px-2 sm:px-3 py-2 rounded-lg border-2 transition-all duration-300 cursor-pointer hover:scale-105 active:scale-95 focus-ring-info ${
                          level === index
                            ? `${data.color} ${data.borderColor} surface-elevated font-bold shadow-lg`
                            : 'text-secondary border-subtle surface-interactive hover:text-primary hover:border-glass hover:surface-hover hover:shadow-md'
                        }`}
                        key={index}
                        onClick={() => handleSetLevel(index)}
                        type="button"
                      >
                        <IconComponent className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-xs font-medium">
                          {data.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="explanation-box transition-all duration-500 ease-in-out text-left">
                <div className="flex items-center gap-2 mb-4">
                  <CurrentIcon
                    className={`w-5 h-5 sm:w-6 sm:h-6 ${currentLevelData.color}`}
                  />
                  <h3
                    className={`font-bold text-base sm:text-lg ${currentLevelData.color} text-left`}
                  >
                    {currentLevelData.label} Explanation
                  </h3>
                </div>
                <div className="text-secondary leading-relaxed text-left prose dark:prose-invert max-w-none text-sm sm:text-base">
                  {renderTextWithMathAndHTML(
                    currentExplanation,
                    `explanation-${level}`
                  )}
                </div>
              </div>

              <div className="mt-4 sm:mt-6 flex justify-center">
                <div className="flex gap-1.5 sm:gap-2">
                  {[0, 1, 2].map(dot => (
                    <div
                      className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                        dot <= level
                          ? level === 0
                            ? 'bg-green-400'
                            : level === 1
                              ? 'bg-yellow-400'
                              : 'bg-red-400'
                          : 'surface-interactive'
                      }`}
                      key={dot}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Screen reader announcement for difficulty level changes */}
            <div
              aria-live="polite"
              className="sr-only"
            >
              {`Now showing ${currentLevelData.label} level explanation.`}
            </div>

            
          </div>
        </div>
      </section>
    );
  }
);

DificultyExplanationSlider.displayName = 'DificultyExplanationSlider';

export default DificultyExplanationSlider;
export type { ExplanationLevels };
